/**
 * Auth Slice - RTK slice for authentication
 * Converted from Redux-Thunk to RTK createAsyncThunk
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { API_URL } from '../../actions/utilAction';
import * as constant from '../../actions/constant';
import history from '../../history';

// TypeScript interfaces
interface AuthData {
  _id: string;
  name: string;
  email: string;
  token: string;
  role?: string;
  profilePic?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  authData: AuthData | null;
  access_token: string | null;
  loading: boolean;
  error: string | null;
}

interface LoginParams {
  email: string;
  password: string;
}

interface ForgotPasswordParams {
  email: string;
}

interface ResetPasswordParams {
  token: string;
  password: string;
  confirmPassword: string;
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('access_token'),
  authData: null,
  access_token: localStorage.getItem('access_token'),
  loading: false,
  error: null,
};

// Async thunks

// Login action
export const loginAction = createAsyncThunk<
  { authData: AuthData; access_token: string },
  LoginParams,
  { rejectValue: string }
>('auth/login', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_LOGIN}`, params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.data.code === 200) {
      localStorage.setItem('access_token', response.data.data.token);
      history.push(constant.DASHBOARD);
      toastr.success(response.data.message);
      return {
        authData: response.data.data,
        access_token: response.data.data.token
      };
    }

    toastr.error(response.data.message);
    return rejectWithValue(response.data.message || 'Login failed');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Forgot password action
export const forgotPassword = createAsyncThunk<
  void,
  ForgotPasswordParams,
  { rejectValue: string }
>('auth/forgotPassword', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.FORGOT_PASSWORD}`, params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.data.code === 200) {
      history.push(constant.LOGIN);
      toastr.success(response.data.message);
      return;
    }

    toastr.error(response.data.message);
    return rejectWithValue(response.data.message || 'Forgot password request failed');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Reset password action
export const resetPassword = createAsyncThunk<
  void,
  ResetPasswordParams,
  { rejectValue: string }
>('auth/resetPassword', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.RESET_PASSWORD}`, params, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.data.code === 200) {
      history.push(constant.LOGIN);
      toastr.success(response.data.message);
      return;
    }

    toastr.error(response.data.message);
    return rejectWithValue(response.data.message || 'Reset password failed');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('access_token');
      state.isAuthenticated = false;
      state.authData = null;
      state.access_token = null;
      history.push(constant.LOGIN);
    },
    clearError: (state) => {
      state.error = null;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAction.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.authData = action.payload.authData;
        state.access_token = action.payload.access_token;
      })
      .addCase(loginAction.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // Forgot password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // Reset password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const { logout, clearError, setAuthenticated } = authSlice.actions;
export default authSlice.reducer;
